import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

/**
 * Local toggle primitive for the Settings page. There is no Switch in
 * src/components/ui, and this feature is scoped to files under
 * src/features/settings/**, so it's built here and reused across every
 * section that needs a boolean toggle (Notifications' email/push grid,
 * etc) rather than duplicated per section.
 * Native <button role="switch"> for full keyboard + screen-reader support,
 * with a short transform transition on the thumb (no bouncy springs).
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "bg-primary" : "bg-muted",
        className
      )}
    >
      <motion.span
        className="inline-block rounded-full bg-white shadow-sm"
        style={{ height: "1.125rem", width: "1.125rem" }}
        animate={{ x: checked ? 22 : 4 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
      />
    </button>
  );
}
